import { Box, Typography, Divider, Tabs } from '@mui/material'
import { FC, useMemo } from 'react'
import Home from '@/components/icons/home'
import { styled } from '@mui/material/styles'
import { useReactiveVar } from '@apollo/client'
import { currentFilters } from '@/state/currentFilters'
import { RentyAiPropertiesFilterBy } from '@/interface/message'
import { statsigLog } from '@/lib/statsigAndGa'
import RentType from '@/components/icons/map/filter/rentType'
import BedroomFilter from './bedroomFilter'
import BudgetFilter from './budgetFilter'
import MoveInDateFilter from './moveInDateFilter'
import { RENTY_TYPE_LABEL } from './filterConfig'

interface FilterProps {
  rentalNum: number
  showFilter: boolean
  showRental: boolean
  onFilterChange: (filters: RentyAiPropertiesFilterBy) => void
}

const StyledTabs = styled(Tabs)(() => ({
  '& .MuiTabs-indicator': {
    backgroundColor: 'transparent' // 隐藏原有的指示器
  },
  '&.MuiTabs-root': {
    position: 'relative',
    minHeight: '32px',
    '& .MuiTabs-scrollButtons:first-of-type': {
      zIndex: 100,
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: '40px',
      backgroundImage: 'linear-gradient(to left,rgb(255 255 255/0),white 20px)',
      margin: 0,
      opacity: 1,
      justifyContent: 'flex-start',
      '&.Mui-disabled': {
        display: 'none'
      },
      '& .MuiTouchRipple-root': {
        display: 'none'
      }
    },
    '& .MuiTabs-scrollButtons:last-of-type': {
      zIndex: 100,
      position: 'absolute',
      right: 0,
      top: 0,
      height: '100%',
      width: '40px',
      backgroundImage: 'linear-gradient(to right,rgb(255 255 255/0),white 20px)',
      margin: 0,
      opacity: 1,
      justifyContent: 'end',
      '&.Mui-disabled': {
        display: 'none'
      },
      '& .MuiTouchRipple-root': {
        display: 'none'
      }
    }
  },
  '& .MuiTabs-scrollButtons .Mui-disabled': {
    display: 'none!important'
  }
}))

const Filter: FC<FilterProps> = props => {
  const { rentalNum, showFilter, showRental, onFilterChange } = props
  const { minPrice, maxPrice, moveInEndTimeEqualTo, bedsFilterBy, isColiving } =
    useReactiveVar(currentFilters)
  const disableResetAll = useMemo(() => {
    if (Number.isInteger(minPrice) || Number.isInteger(maxPrice)) return false
    if (moveInEndTimeEqualTo) return false
    if (isColiving !== null) return false
    if (bedsFilterBy && Array.isArray(bedsFilterBy) && bedsFilterBy.length > 0) return false
    return true
  }, [minPrice, maxPrice, moveInEndTimeEqualTo, bedsFilterBy, isColiving])

  const onApplyFilter = (filters: RentyAiPropertiesFilterBy, statsigName?: string) => {
    const oldFilters = currentFilters()
    const newFilters = { ...oldFilters, ...filters }
    // 1. 发送新的filter到后端 send message
    // 2. 地图根据新的filter变化
    if (statsigName) {
      statsigLog(statsigName, newFilters)
    }
    onFilterChange(newFilters)
  }
  const onResetAll = () => {
    if (disableResetAll) return
    const oldFilters = currentFilters()
    const newFilters = {
      ...oldFilters,
      minPrice: null,
      maxPrice: null,
      isColiving: null,
      moveInEndTimeEqualTo: null,
      moveInStartTimeEqualTo: null,
      bedsFilterBy: []
    }
    statsigLog('reset_all_filter_click', newFilters)
    onFilterChange(newFilters)
  }

  if (!showFilter && !showRental) {
    return null
  }
  return (
    <Box
      component="div"
      className={
        showFilter
          ? 'w-full bg-white flex px-5 py-2 items-center gap-2'
          : 'z-10 absolute left-5 top-2 flex items-center gap-1 rounded-lg bg-primary bg-opacity-50 px-3 py-1'
      }
    >
      {showRental && (
        <>
          <Home
            className={showFilter ? 'w-4 h-4 text-secondary' : 'w-4 h-4 text-white'}
            fill="#7171717"
          />
          <Typography
            className={`${showFilter ? 'text-secondary' : 'text-white'} text-sm whitespace-nowrap`}
          >
            {rentalNum} Rentals
          </Typography>
        </>
      )}
      {showFilter && (
        <>
          <Divider light flexItem orientation="vertical" className="mx-1 h-4 self-center" />
          <StyledTabs scrollButtons variant="scrollable" className="flex-1" value={0}>
            <Box component="div" className="flex items-center gap-2">
              <Box
                component="button"
                onClick={() => {
                  onApplyFilter(
                    { isColiving: isColiving === false ? null : false },
                    'whole_unit_filter_apply_click'
                  )
                }}
                className={`flex items-center gap-2 py-[6px] px-4 border border-gray-200 rounded-20 hover:bg-gray-100 ${
                  isColiving === false ? 'border-primary bg-gray-100' : 'border-gray-200'
                }`}
              >
                <RentType className="w-4 h-4" />
                <Typography className="text-primary text-sm">{RENTY_TYPE_LABEL}</Typography>
              </Box>
              <BedroomFilter
                bedroom={bedsFilterBy}
                onApply={beds =>
                  onApplyFilter({ bedsFilterBy: beds }, 'bedroom_filter_apply_click')
                }
              />
              <BudgetFilter
                minPrice={minPrice}
                maxPrice={maxPrice}
                onApply={budget => {
                  onApplyFilter(
                    { minPrice: budget.minPrice, maxPrice: budget.maxPrice },
                    'budget_filter_apply_click'
                  )
                }}
              />
              <MoveInDateFilter
                moveInDate={moveInEndTimeEqualTo}
                onApply={endTime => {
                  onApplyFilter(
                    { moveInEndTimeEqualTo: endTime, moveInStartTimeEqualTo: endTime },
                    'move_in_date_filter_apply_click'
                  )
                }}
              />
            </Box>
          </StyledTabs>
          <Typography
            onClick={onResetAll}
            className={`capitalize underline text-sm font-semibold whitespace-nowrap ${
              disableResetAll ? 'text-tertiary cursor-default' : 'text-primary cursor-pointer'
            }`}
          >
            Reset All
          </Typography>
        </>
      )}
    </Box>
  )
}

export default Filter
